import { GridCellParams, GridColDef, GridPreProcessEditCellProps, GridRenderCellParams } from '@mui/x-data-grid';
import React from 'react';
import { useSelector } from 'react-redux';

import { hasRight } from '#app/Common/UserCommon';
import DataGrid from '#app/Component/DataGrid';
import IconAction from '#app/Component/IconAction';
import { StateType } from '#app/Store';
import { momentDateFormatter } from '#app/Util/MomentDateFormatter';
import { isOnlyDigits } from '#app/Util/NumberTool';
import Scan from '#app/View/InventoryManagement/Inventory/DepositInventory/components/Scan';
import useActions from '#app/View/InventoryManagement/Inventory/DepositInventory/hooks/useActions';
import { Filters } from '#app/View/InventoryManagement/Inventory/DepositInventory/hooks/useInventory';
import { useCollectionPaginated } from '#app/hooks/useCollectionPaginated';
import { InventoryInfo } from '#app/typings/deposit';

type Props = Omit<ReturnType<typeof useCollectionPaginated<InventoryInfo, Filters>>, 'filters' | 'setFilters'>;

const List: React.FC<Props> = ({ data, loading, mutate, sorting, setSorting }) => {
  const { update, cancel } = useActions(mutate);
  const authenticationReducer = useSelector((state: StateType) => state.AuthenticationReducer);

  const columns: GridColDef[] = [
    {
      field: 'sku',
      headerName: 'SKU',
      minWidth: 160,
      sortable: false,
    },
    {
      field: 'name',
      headerName: 'Nom',
      flex: 1,
    },
    {
      field: 'flavor',
      headerName: 'Parfum / Couleur',
      minWidth: 200,
      sortable: false,
      flex: 1,
    },
    {
      field: 'packSize',
      headerName: 'Conditionnement',
      minWidth: 200,
      sortable: false,
    },
    {
      field: 'mark',
      headerName: 'Marque',
    },
    ...(!authenticationReducer.shopSelected
      ? [
          {
            field: 'storageLocation',
            headerName: 'Emplacement',
            minWidth: 130,
            editable:
              hasRight(authenticationReducer, 'ROLE_INVENTORY_MANAGEMENT_INVENTORY') ||
              hasRight(authenticationReducer, 'ROLE_INVENTORY_MANAGEMENT_INVENTORY_ADD'),
          },
          {
            field: 'batch',
            headerName: 'Lot',
            minWidth: 160,
            sortable: false,
          },
          {
            field: 'date',
            headerName: 'Date du lot',
            valueGetter: (value: string | Date) => momentDateFormatter.dateTimeToFormatFr(value),
          },
        ]
      : []),
    {
      field: 'quantity',
      headerName: 'Quantité',
      editable:
        hasRight(authenticationReducer, 'ROLE_INVENTORY_MANAGEMENT_INVENTORY') ||
        hasRight(authenticationReducer, 'ROLE_INVENTORY_MANAGEMENT_INVENTORY_ADD'),
      preProcessEditCellProps: (params: GridPreProcessEditCellProps) => ({
        ...params.props,
        error: !isOnlyDigits(params.props.value),
      }),
    },
    ...(!authenticationReducer.shopSelected ||
    ((hasRight(authenticationReducer, 'ROLE_INVENTORY_MANAGEMENT_INVENTORY') ||
      hasRight(authenticationReducer, 'ROLE_INVENTORY_MANAGEMENT_INVENTORY_VALIDATED')) &&
      (authenticationReducer.rightShops?.region.includes(authenticationReducer.shopSelected) ||
        authenticationReducer.rightShops?.national.includes(authenticationReducer.shopSelected)))
      ? [
          {
            field: 'stock',
            headerName: 'En stock',
            renderCell: ({ value, row }: GridRenderCellParams<InventoryInfo, number>) => (
              <span>
                {value} {row.quantity !== null ? `(${row.quantity - (value ?? 0)})` : ''}
              </span>
            ),
            cellClassName: ({ value, row }: GridCellParams<InventoryInfo, number>) =>
              row.quantity !== null ? (row.quantity - (value ?? 0) >= 0 ? 'positive' : 'negative') : 'notChange',
          },
        ]
      : []),
    {
      field: 'action',
      headerName: 'Action',
      type: 'actions',
      width: 70,
      getActions: ({ row }) => [<IconAction title="Annuler" icon="cancel" onClick={async () => await cancel(row)} />],
    },
  ];

  return (
    <>
      <Scan
        onUpdate={async (ean) => {
          const row = data.find((item) => item.ean === ean);
          if (!row) {
            return false;
          }

          try {
            await update(row);
            return true;
          } catch {
            return false;
          }
        }}
      />
      <DataGrid
        sx={{
          '& .positive, & .negative, & .notChange': {
            fontWeight: 'bold',
            color: '#fff',
          },
          '& .positive': {
            backgroundColor: '#006500',
          },
          '& .negative': {
            backgroundColor: '#982525',
          },
          '& .notChange': {
            backgroundColor: '#17a2b8',
          },
        }}
        rowHeight={35}
        rows={data}
        hideFooter
        columns={columns}
        loading={loading}
        sorting={sorting}
        setSorting={setSorting}
        paginationMode="server"
        rowCount={data.length}
        processRowUpdate={async (updatedRow) => await update(updatedRow as InventoryInfo)}
      />
    </>
  );
};

export default List;
